import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { DateTime } from 'luxon';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { TranslateModule } from '@ngx-translate/core';

// TODO: Ajustar imports para apontar para libs do monorepo
// import { convertToLocal, dateTimeNow, isSameDay } from '@nomoh-monorepo/shared-util-tools';
import {
  AgendaDataService,
  EstabelecimentoDataService,
} from '@nomoh-monorepo/data-access-bardo-administrador';

// TODO: Service que precisa ser copiado
// import { AgendaFiltroService } from '../agenda-filtro/service/agenda-filtro.service';

interface DiaCalendario {
  data: Date;
  dia: number;
  diaSemana: string;
  isHoje: boolean;
  isSelecionado: boolean;
  isOutroMes: boolean;
  isFeriado: boolean;
}

interface TouchPosition {
  x: number;
  y: number;
}

@Component({
  standalone: true,
  imports: [CommonModule, ButtonModule, RippleModule, TranslateModule],
  selector: 'lib-calendario-semanal',
  templateUrl: './calendario-semanal.component.html',
  styleUrls: ['./calendario-semanal.component.scss'],
})
export class CalendarioSemanalComponent implements OnInit, OnDestroy {
  private readonly timeZonePadrao = 'America/Sao_Paulo';

  @ViewChild('daysContainer', { static: true })
  daysContainer!: ElementRef<HTMLDivElement>;

  visualizarDiasConfirmados = false;
  datasMarcadas: Date[] = [];

  // Inputs
  $dataSelecionada = input<Date>(new Date()); // TODO: usar dateTimeNow() quando disponível

  // Outputs
  semanaAlterada = output<{ inicio: Date; fim: Date }>();

  // Signals internos
  private dataAtual = signal(new Date());
  private inicioSemana = signal(new Date());
  private modoVisualizacao = signal<'semana' | 'mes'>('semana');
  private feriadosDatas = signal<string[]>([]);

  // Touch/Swipe state
  private touchStart = signal<TouchPosition | null>(null);
  private touchEnd = signal<TouchPosition | null>(null);
  private isSwipeEnabled = signal(true);
  private swipeThreshold = 50; // Distância mínima para considerar um swipe
  private swipeDirection = signal<'left' | 'right' | null>(null);
  $iconVisualizarDiasConfirmados = signal('pi pi-eye');

  // Computed values
  diasSemana = computed(() => this.calcularDiasSemana());
  $periodLabel = computed(() => this.montarPeriodoLabel());

  // Injection
  // agendaFiltroService = inject(AgendaFiltroService); // TODO
  AgendaDataService = inject(AgendaDataService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);

  // Touch event listeners
  private touchStartListener?: (e: TouchEvent) => void;
  private touchMoveListener?: (e: TouchEvent) => void;
  private touchEndListener?: (e: TouchEvent) => void;

  constructor() {
    // Effect para atualizar quando a data selecionada mudar
    effect(
      () => {
        const data = this.$dataSelecionada();
        this.dataAtual.set(data);
        this.atualizarInicioSemana(data);
      }
    );

    effect(() => {
      const agenda = this.AgendaDataService.$agendaInDb();
      const agendamentos = agenda.agendamentos ?? [];

      this.datasMarcadas = agendamentos
        .map((agendamento) => this.toCalendarDate(agendamento.dataHora))
        .filter((data): data is Date => !!data);
    });

    effect(
      () => {
        const visualizar = this.AgendaDataService.$visualizarDiasConfirmados();
        this.visualizarDiasConfirmados = visualizar;
        this.$iconVisualizarDiasConfirmados.set(
          visualizar ? 'pi pi-eye' : 'pi pi-eye-slash'
        );
      }
    );

    effect(() => {
      const feriados = this.AgendaDataService.$feriados();
      const datas = feriados
        .map((feriado) => {
          if (!feriado?.date) {
            return null;
          }

          const parsed = DateTime.fromISO(feriado.date, { zone: 'utc' });
          return parsed.isValid ? parsed.toISODate() : null;
        })
        .filter((value): value is string => Boolean(value));

      this.feriadosDatas.set(datas);
    });

    // Inicializar
    this.atualizarInicioSemana(this.$dataSelecionada());
  }

  ngOnInit(): void {
    this.setupTouchEvents();
  }

  ngOnDestroy(): void {
    this.removeTouchEvents();
  }

  private setupTouchEvents(): void {
    const container = this.daysContainer.nativeElement;

    this.touchStartListener = (e: TouchEvent) => this.onTouchStart(e);
    this.touchMoveListener = (e: TouchEvent) => this.onTouchMove(e);
    this.touchEndListener = () => this.onTouchEnd();

    container.addEventListener('touchstart', this.touchStartListener, {
      passive: true,
    });
    container.addEventListener('touchmove', this.touchMoveListener, {
      passive: false,
    });
    container.addEventListener('touchend', this.touchEndListener, {
      passive: true,
    });
  }

  private removeTouchEvents(): void {
    const container = this.daysContainer.nativeElement;

    if (this.touchStartListener) {
      container.removeEventListener('touchstart', this.touchStartListener);
    }
    if (this.touchMoveListener) {
      container.removeEventListener('touchmove', this.touchMoveListener);
    }
    if (this.touchEndListener) {
      container.removeEventListener('touchend', this.touchEndListener);
    }
  }

  private onTouchStart(e: TouchEvent): void {
    if (!this.isSwipeEnabled()) return;

    const touch = e.touches[0];
    this.touchStart.set({
      x: touch.clientX,
      y: touch.clientY,
    });
    this.touchEnd.set(null);
    this.swipeDirection.set(null);
  }

  private onTouchMove(e: TouchEvent): void {
    if (!this.isSwipeEnabled() || !this.touchStart()) return;

    const touch = e.touches[0];
    const currentTouch = {
      x: touch.clientX,
      y: touch.clientY,
    };

    const startTouch = this.touchStart();
    if (!startTouch) return;

    const deltaX = Math.abs(currentTouch.x - startTouch.x);
    const deltaY = Math.abs(currentTouch.y - startTouch.y);

    // Se o movimento horizontal for maior que o vertical, prevenir scroll vertical
    if (deltaX > deltaY && deltaX > 10) {
      e.preventDefault();
    }

    this.touchEnd.set(currentTouch);
  }

  private onTouchEnd(): void {
    if (!this.isSwipeEnabled() || !this.touchStart() || !this.touchEnd())
      return;

    const startTouch = this.touchStart();
    const endTouch = this.touchEnd();
    if (!startTouch || !endTouch) return;

    const deltaX = endTouch.x - startTouch.x;
    const deltaY = endTouch.y - startTouch.y;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Verificar se é um swipe horizontal válido
    if (absDeltaX > this.swipeThreshold && absDeltaX > absDeltaY) {
      if (deltaX > 0) {
        // Swipe para direita - semana anterior
        this.swipeDirection.set('right');
        this.navegarSemana(-1);
      } else {
        // Swipe para esquerda - próxima semana
        this.swipeDirection.set('left');
        this.navegarSemana(1);
      }

      // Feedback visual temporário
      this.addSwipeFeedback();
    }

    // Resetar estado do touch
    this.touchStart.set(null);
    this.touchEnd.set(null);
  }

  private addSwipeFeedback(): void {
    const container = this.daysContainer.nativeElement;
    const direction = this.swipeDirection();

    if (direction) {
      container.classList.add(`swipe-${direction}`);

      // Remover a classe após a animação
      setTimeout(() => {
        container.classList.remove(`swipe-${direction}`);
        this.swipeDirection.set(null);
      }, 300);
    }
  }

  private atualizarInicioSemana(data: Date): void {
    const inicio = this.obterInicioSemana(data);
    this.inicioSemana.set(inicio);

    const fim = new Date(inicio);
    fim.setDate(fim.getDate() + 6);

    this.semanaAlterada.emit({ inicio, fim });
  }

  private obterInicioSemana(data: Date): Date {
    const inicio = new Date(data);
    const diaSemana = inicio.getDay();
    // Ajustar para começar na segunda-feira (0 = domingo, 1 = segunda)
    const diasParaSubtrair = diaSemana === 0 ? 6 : diaSemana - 1;
    inicio.setDate(inicio.getDate() - diasParaSubtrair);
    return inicio;
  }

  // TODO: Substituir por utilities do monorepo quando disponíveis
  private calcularDiasSemana(): DiaCalendario[] {
    const dias: DiaCalendario[] = [];
    const inicioSemana = new Date(this.inicioSemana());
    const hoje = new Date();
    const dataAtual = new Date(this.dataAtual());

    const nomesDias = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

    for (let i = 0; i < 7; i++) {
      const data = new Date(inicioSemana);
      data.setDate(data.getDate() + i);

      dias.push({
        data: data,
        dia: data.getDate(),
        diaSemana: nomesDias[i],
        isHoje: this.isMesmaData(data, hoje),
        isSelecionado: this.isMesmaData(data, dataAtual),
        isOutroMes: data.getMonth() !== dataAtual.getMonth(),
        isFeriado: this.isFeriado(data),
      });
    }

    return dias;
  }

  private isMesmaData(data1: Date, data2: Date): boolean {
    return this.toCalendarDateTime(data1).hasSame(
      this.toCalendarDateTime(data2),
      'day'
    );
  }

  private isFeriado(data: Date): boolean {
    const dataISO = this.toCalendarDateTime(data).toISODate();
    if (!dataISO) {
      return false;
    }

    return this.feriadosDatas().includes(dataISO);
  }

  private toCalendarDate(
    valor: Date | string | null | undefined
  ): Date | null {
    if (!valor) {
      return null;
    }

    const dateTime =
      valor instanceof Date
        ? DateTime.fromJSDate(valor)
        : DateTime.fromISO(valor);

    if (!dateTime.isValid) {
      return null;
    }

    const local = dateTime.setZone(this.getTimeZoneEstabelecimento());
    return new Date(local.year, local.month - 1, local.day, 12);
  }

  private toCalendarDateTime(data: Date): DateTime {
    return DateTime.fromObject(
      {
        year: data.getFullYear(),
        month: data.getMonth() + 1,
        day: data.getDate(),
      },
      { zone: this.getTimeZoneEstabelecimento() }
    );
  }

  private getTimeZoneEstabelecimento(): string {
    const timeZone =
      this.estabelecimentoDataService
        .$estabelecimentoAtual()
        ?.timeZone?.trim() || this.timeZonePadrao;
    return DateTime.now().setZone(timeZone).isValid
      ? timeZone
      : this.timeZonePadrao;
  }

  private montarPeriodoLabel(): string {
    const modo = this.modoVisualizacao();
    const data = this.dataAtual();

    if (modo === 'mes') {
      // Modo mês: mostrar apenas o mês do dia selecionado
      const labelMes = new Intl.DateTimeFormat('pt-BR', { month: 'long' })
        .format(data)
        .toLowerCase();
      return `${labelMes} ${data.getFullYear()}`;
    } else {
      // Modo semana: mostrar período da semana
      const inicioSemana = this.inicioSemana();
      const fimSemana = new Date(inicioSemana);
      fimSemana.setDate(fimSemana.getDate() + 6);

      // Se a semana estiver no mesmo mês
      if (inicioSemana.getMonth() === fimSemana.getMonth()) {
        const mes = new Intl.DateTimeFormat('pt-BR', { month: 'long' })
          .format(inicioSemana)
          .toLowerCase();
        return `${inicioSemana.getDate()}-${fimSemana.getDate()} ${mes} ${inicioSemana.getFullYear()}`;
      } else {
        // Semana atravessa meses
        const mesInicio = new Intl.DateTimeFormat('pt-BR', {
          month: 'short',
        }).format(inicioSemana);
        const mesFim = new Intl.DateTimeFormat('pt-BR', {
          month: 'short',
        }).format(fimSemana);
        return `${inicioSemana.getDate()} ${mesInicio} - ${fimSemana.getDate()} ${mesFim}`;
      }
    }
  }

  // Métodos públicos (chamados pelo template)
  selecionarDia(dia: DiaCalendario): void {
    this.dataAtual.set(dia.data);

    // ✅ Atualizar AgendaService para sincronizar com outros componentes
    this.AgendaDataService.setDataSelecionada(dia.data);

    // TODO: Descomentar quando AgendaFiltroService estiver disponível
    // this.agendaFiltroService.alterarData(dia.data);
  }

  navegarSemana(direcao: number): void {
    const novaData = new Date(this.dataAtual());

    if (this.modoVisualizacao() === 'semana') {
      // Navegar por semanas
      novaData.setDate(novaData.getDate() + direcao * 7);
    } else {
      // Navegar por meses
      novaData.setMonth(novaData.getMonth() + direcao);
    }

    this.dataAtual.set(novaData);
    this.atualizarInicioSemana(novaData);

    // ✅ Atualizar AgendaService para sincronizar com outros componentes
    this.AgendaDataService.setDataSelecionada(novaData);
  }

  irParaHoje(): void {
    const hoje = new Date(); // TODO: usar dateTimeNow() quando disponível
    this.dataAtual.set(hoje);
    this.atualizarInicioSemana(hoje);

    // ✅ Atualizar AgendaService para sincronizar com outros componentes
    this.AgendaDataService.setDataSelecionada(hoje);

    // TODO: Descomentar quando AgendaFiltroService estiver disponível
    // this.agendaFiltroService.alterarData(hoje);
  }

  alternarVisualizacao(): void {
    const modoAtual = this.modoVisualizacao();
    const novoModo = modoAtual === 'semana' ? 'mes' : 'semana';
    this.modoVisualizacao.set(novoModo);
  }

  // Métodos utilitários para debug (podem ser removidos em produção)
  enableSwipe(): void {
    this.isSwipeEnabled.set(true);
  }

  disableSwipe(): void {
    this.isSwipeEnabled.set(false);
  }

  dateClass(dataSemana: Date): boolean {
    if (this.visualizarDiasConfirmados) {
      // Usamos .some() para verificar se algum item no array corresponde à condição
      const temAgendamento = this.datasMarcadas.some(
        (dataMarcada) =>
          dataMarcada.getDate() === dataSemana.getDate() &&
          dataMarcada.getMonth() === dataSemana.getMonth() &&
          dataMarcada.getFullYear() === dataSemana.getFullYear()
      );

      if (temAgendamento) {
        return true;
      }
    }

    return false;
  }

  alternarVisualizacaoDiasConfirmados() {
    this.AgendaDataService.setVisualizarDiasConfirmados(
      !this.AgendaDataService.$visualizarDiasConfirmados()
    );
  }
}
